import { useId, useRef } from 'react';

import type { StaffOption } from '../../api/staff-options';
import { VisuallyHiddenStatus } from '../shared/VisuallyHiddenStatus';

import PeoplePickerClearButton from './PeoplePickerClearButton';
import PeoplePickerInput from './PeoplePickerInput';
import PeoplePickerList from './PeoplePickerList';
import { usePeoplePicker } from './use-people-picker';

interface Props {
  label: string;
  hint?: string;
  value: string;
  valueName: string;
  /** Offer a Clear button; the owner is required, so it goes without. */
  clearable: boolean;
  required?: boolean;
  error?: string;
  onChange: (user: StaffOption | null) => void;
}

function searchAnnouncement(open: boolean, status: string, count: number): string {
  if (!open) return '';
  if (status === 'loading') return 'Searching…';
  if (status === 'failed') return 'Could not load people';
  return count === 0 ? 'No people found' : `${count} people available`;
}

/** A searchable list of active users, one person chosen. Clicking or typing
 *  opens the list; the chosen person's name shows until the RD types. */
export default function PeoplePickerField(props: Props) {
  const { label, hint, value, valueName, clearable, required, error, onChange } = props;
  const uid = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const picker = usePeoplePicker(onChange);
  const activeId = picker.users[picker.active] ? `${uid}-list-${picker.active}` : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
      <label htmlFor={`${uid}-input`} style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nb-ink-2)' }}>
          {label}
          {required && <span style={{ color: 'var(--nb-warn)' }}> *</span>}
        </span>
        {hint && <span style={{ fontSize: 10.5, color: 'var(--nb-subtle)' }}>{hint}</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <PeoplePickerInput
          id={`${uid}-input`}
          inputRef={inputRef}
          listId={`${uid}-list`}
          activeId={activeId}
          open={picker.open}
          text={picker.query ?? valueName}
          hasValue={Boolean(value)}
          padRight={clearable}
          required={Boolean(required)}
          error={error}
          errorId={`${uid}-error`}
          onOpen={picker.openList}
          onClose={picker.close}
          onSearch={picker.search}
          onKeyDown={picker.onKeyDown}
        />
        {clearable && value && (
          <PeoplePickerClearButton
            label={label}
            onClear={() => {
              onChange(null);
              inputRef.current?.focus();
            }}
          />
        )}
        {picker.open && (
          <PeoplePickerList
            id={`${uid}-list`}
            label={label}
            options={picker.users}
            active={picker.active}
            selectedId={value}
            status={picker.status}
            onPick={picker.pick}
          />
        )}
      </div>
      <VisuallyHiddenStatus
        message={searchAnnouncement(picker.open, picker.status, picker.users.length)}
      />
      {error && (
        <span id={`${uid}-error`} role="alert" style={{ fontSize: 10.5, color: 'var(--nb-risk)' }}>
          {error}
        </span>
      )}
    </div>
  );
}
